import React, { useState, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material'
import {
  Language,
  Phone,
  Email,
  LocationOn,
  Article,
  AttachMoney,
  Facebook,
  Instagram,
  LinkedIn,
} from '@mui/icons-material'

import { queries } from '../graphql'
import { CategoryChip, DefaultLogo } from './charity-portal/charity-category-icons'
import ConfirmationModal from '../components/confirmation-modal'

function IconText({ icon: Icon, primary, secondary }) {
  return (
    <ListItem>
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={primary} secondary={secondary} />
    </ListItem>
  )
}

function getBlueFilter() {
  return 'brightness(0) saturate(100%) invert(67%) sepia(72%) saturate(380%) hue-rotate(165deg) brightness(97%) contrast(96%)'
}

export function SocialMediaIcon({
  platform,
  link,
  styles = {
    color: 'white',
    border: '1px solid white',
    width: 60,
    height: 60,
    '&:hover': {
      bgcolor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}) {
  const [showModal, setShowModal] = useState(false)

  const icons = {
    Facebook,
    Instagram,
    Linkedin: LinkedIn,
    Website: Language,
  }

  const Icon = icons[platform]
  if (!Icon) return null

  const formattedLink = link && !link.match(/^https?:\/\//) ? `https://${link}` : link

  function hideConfirmationModal(preference) {
    localStorage.setItem('ExternalLinkHideModalPreference', preference)
    setShowModal(false)
  }

  function navigateToLink(preference) {
    window.open(formattedLink, '_blank', 'noopener,noreferrer')
    hideConfirmationModal(preference)
  }

  function attemptNavigationToExternalLink() {
    const hideModal = JSON.parse(localStorage.getItem('ExternalLinkHideModalPreference'))
    if (hideModal) navigateToLink(true)
    else setShowModal(true)
  }

  return (
    <>
      {platform === 'Website' ? (
        <Chip
          icon={<Icon sx={{ color: 'white' }} />}
          label={link}
          component="a"
          onClick={() => attemptNavigationToExternalLink()}
          sx={{
            color: 'white',
            height: 60,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
            bgcolor: 'transparent',
            border: '1px solid white',
            '& .MuiChip-icon': {
              color: 'white',
            },
          }}
          clickable
        />
      ) : (
        <IconButton onClick={() => attemptNavigationToExternalLink()} sx={styles}>
          <Icon />
        </IconButton>
      )}
      {showModal && (
        <ConfirmationModal
          title="Are you sure you want to leave this site?"
          description={`By accepting you'd be leaving this site and being redirected to ${formattedLink}`}
          onConfirm={(preference) => navigateToLink(preference)}
          onCancel={(preference) => hideConfirmationModal(preference)}
        />
      )}
    </>
  )
}

export function LandingPage({ charityIdProp, fundIdProp }) {
  const params = useParams()
  const charityId = charityIdProp || params.charityId
  const fundId = fundIdProp || params.fundId
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)

  const { data: { charity } = {}, loading: charityLoading } = useQuery(queries.charities.charity, {
    variables: { charityId, fundId: Number(fundId) },
  })

  const { data: descriptionData, loading: descriptionLoading } = useQuery(queries.charities.getMyCharityDescription, {
    variables: { id: charityId },
  })

  const { data: charityProfileData } = useQuery(queries.charities.getMyCharityProfile, {
    variables: { id: charityId },
  })

  const { data: categoriesData, loading: categoriesLoading } = useQuery(queries.charities.getMyCharityCategories, {
    variables: { charityId },
  })

  if (charityLoading || descriptionLoading || categoriesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={150} thickness={3} />
      </Box>
    )
  }

  const description = descriptionData?.getMyCharityDescription
  const charityProfile = charityProfileData?.getMyCharityProfile
  const logo = charity?.logo
  const socialMedia = charity?.socialMediaLinks
  const categories = categoriesData?.getMyCharityCategories?.map((item) => item.category) || []

  if (!charity) return null

  const fullAddress = `${charity.mailingAddress}, ${charity.city}, ${charity.province} ${charity.postalCode}`

  const getLogoSource = () => {
    if (!logo) return null

    if (logo?.logo) {
      try {
        return `data:image/png;base64,${logo.logo}`
      } catch (e) {
        console.error('Error loading logo:', e)
        return null
      }
    }

    if (logo?.logoPath) {
      return logo.logoPath
    }

    return null
  }

  const logoSrc = getLogoSource()

  function categoryChipComponent() {
    const colours = ['#293860', '#64bee5', '#eccb48', '#e67453']
    colours.sort(() => Math.random() - 0.5)
    const categoryElements = []

    for (let i = 0; i < categories.length; i += 1) {
      const category = categories[i]
      const colorIndex = i % colours.length
      categoryElements.push(<CategoryChip key={category.id} name={category.name} color={colours[colorIndex]} />)
    }
    return categoryElements
  }

  return (
    <Container maxWidth="lg">
      <Card elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header Section */}
        <Card
          sx={{
            mb: 4,
            p: 3,
            bgcolor: '#293860',
            borderRadius: {
              xs: 0,
              lg: '9999px',
              boxShadow: 1,
            },
            color: 'white',
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={2.5} justifyItems={{ xs: 'center', md: 'baseline' }}>
              {/* Logo */}
              <Box
                sx={{
                  width: '160px',
                  height: '160px',
                  // make dynamic based on logo color
                  backgroundColor: charityProfile?.logoBackgroundColor || 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {logoSrc ? (
                  <Box
                    component="img"
                    src={logoSrc}
                    alt={`${charity.accountName} logo`}
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = DefaultLogo(categories[0]?.name)
                    }}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      // border: '2px solid white',
                      borderRadius: logoSrc === DefaultLogo(categories[0]?.name) ? '50%' : '8px',
                      opacity: isLoading ? 0 : 1,
                      transition: 'opacity 0.3s ease-in-out',
                      filter: logoSrc === DefaultLogo(categories[0]?.name) ? getBlueFilter() : 'none',
                    }}
                    onLoad={(e) => {
                      setIsLoading(false)
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={DefaultLogo(categories[0]?.name)}
                    alt="Default logo"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '50%',
                      filter: getBlueFilter(),
                    }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6.5} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
                {charity.accountName}
              </Typography>
              <Typography variant="body3" gutterBottom sx={{ mb: 1 }}>
                {charity.id}
              </Typography>
              <br />
              <br />
              <br />
              {description?.tagline && (
                <Typography variant="h6" color="white" sx={{ fontStyle: 'italic', mb: 3 }}>
                  {description.tagline}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                sx={{
                  alignItems: 'center',
                  width: '100%',
                  mt: { xs: 3, md: 0 },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={1}
                  sx={{
                    width: '100%',
                  }}
                >
                  {socialMedia?.map((social) => (
                    <SocialMediaIcon key={social.id} platform={social.platform} link={social.link} />
                  ))}
                </Box>
                {charity.websiteAddress && <SocialMediaIcon platform="Website" link={charity.websiteAddress} />}
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Main Content */}

        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            <Card
              gutterBottom
              sx={{
                flex: 1,
                mb: 4,
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                height: '100%',
              }}
            >
              {/* Categories */}
              <Box>
                <Box
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  mb={2}
                  sx={{
                    justifyContent: {
                      xs: 'center',
                      md: 'flex-start',
                    },
                  }}
                >
                  {categoryChipComponent()}
                </Box>
              </Box>

              {/* Description */}
              <Box mb={2} mt={1}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description?.description}
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            <Card elevation={0}>
              <Box sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    component="a"
                    onClick={() => {
                      if (fundId) {
                        navigate('give')
                      } else {
                        window.location.href = `${process.env.REACT_APP_SITE_URL}/gift/charity/${charityId}`
                      }
                    }}
                    sx={{
                      width: '100%',
                      height: '80px',
                      mt: { xs: 2, md: 2 },
                      mb: { xs: 2, md: 2 },
                      boxShadow: 1,
                      bgcolor: '#293860',
                      textDecoration: 'none',
                    }}
                  >
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Give
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Card>
            {/* Gift History */}
            <Card sx={{ mb: 4, p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="h6" gutterBottom>
                Gift History for this Charity
              </Typography>
              <List>
                <IconText
                  icon={AttachMoney}
                  primary="Year to Date Gifts"
                  secondary={`$${charity.totalGrantRequestsForCharityYTD || 0}`}
                />
                <IconText
                  icon={AttachMoney}
                  primary="Lifetime Gifts"
                  secondary={`$${charity.totalGrantRequestsForCharity || 0}`}
                />
              </List>
            </Card>

            {/* Contact Information */}
            <Card sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="h6" gutterBottom>
                About
              </Typography>
              <List>
                {charity.emailAddress && <IconText icon={Email} primary="Email" secondary={charity.emailAddress} />}
                {charity.contactPhoneNumber && (
                  <IconText icon={Phone} primary="Phone" secondary={charity.contactPhoneNumber} />
                )}
                {fullAddress && fullAddress !== ', ' && (
                  <IconText icon={LocationOn} primary="Address" secondary={fullAddress} />
                )}
                {charity.websiteAddress && (
                  <IconText icon={Language} primary="Website" secondary={charity.websiteAddress} />
                )}
                {charity.id && <IconText icon={Article} primary="Registered Charity Number" secondary={charity.id} />}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Card>
    </Container>
  )
}

export default LandingPage
